<template>
  <UseFocusTrap
    v-if="!isAlertModalOpen"
    :options="{
      immediate: true,
      onDeactivate: handleModalClose,
      initialFocus: () => initialFocusableItem,
      fallbackFocus: () => initialFocusableItem,
    }"
  >
    <div
      :style="{ ...cssVars, '--form-link-color': linkColor }"
      class="form-modal-v2"
      role="dialog"
      :aria-labelledby="formTitle"
      aria-modal="true"
      @wheel.self.prevent
      @touchmove.self.prevent
    >
      <div
        ref="formTitleRef"
        class="sr-only"
        aria-live="polite"
        role="alert"
      />
      <div
        data-testid="forms-modal-container"
        class="form-modal-v2_container"
        tabindex="-1"
        @wheel="setAllowParentScroll(false)"
      >
        <div class="form-modal-v2_controls">
          <button
            ref="closeButtonRef"
            class="close-icon"
            @click="handleModalClose"
          >
            <modal-close-icon
              data-testid="forms-modal-close"
              class="form-modal-v2_close"
            />
          </button>
        </div>
        <div class="form-modal-v2_form">
          <div
            v-if="shouldDisplayStatusView"
            class="form-modal-v2_status-display"
          >
            <h3
              :id="formTitle"
              class="form-modal-v2_title"
            >
              {{ formTitle }}
            </h3>
            <div class="form-modal-v2_status-content">
              <component
                :is="statusIconComponent"
                v-if="hasSubmissionError || submitSuccess"
                class="form-modal-v2_status-icon"
              />
              <p
                aria-live="polite"
                role="alert"
                class="form-modal-v2_status-text"
              >
                {{ statusText }}
              </p>
              <form-button
                v-if="hasSubmissionError"
                class="form-modal-v2_status-action"
                @click="resetForm"
              >
                {{ $t('forms.tryAgain') }}
              </form-button>
            </div>
            <a
              :href="privacyPolicyUrl"
              :target="privacyPolicyTarget"
              class="form-modal-v2_privacy-policy"
            >
              {{ $t('forms.privacyPolicy') }}
            </a>
          </div>

          <fc-form-renderer
            v-else-if="answers.length && !fetchingBrand"
            v-model:answers="answers"
            v-model:answers-form-data-format="answersFormData"
            :branding="branding"
            :close-date="closeDate"
            :description="getDescription"
            :form="form"
            :logo="logo"
            :questions="questions"
            :primary-color="primaryColor"
            :title="formTitle"
            :privacy-policy-url="privacyPolicyUrl"
            :privacy-policy-target="privacyPolicyTarget"
            :hide-header="!logo || !branding"
            :invalid-otp-code="invalidOtpCode"
            @submit="handleFormSubmit"
            @send-otp-code="sendCode"
            @resend-otp-code="sendCode"
            @confirm-otp-code="confirmCode"
            @close-modal="handleModalClose"
          />
        </div>
      </div>
    </div>
  </UseFocusTrap>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useMainStore } from '../../../stores/mainStore.js';
import { useNuxtApp, useRuntimeConfig } from '#imports';
import { useAlertBannerStore } from '../../../stores/alertBannerStore.js';
import { isBefore, parseISO } from 'date-fns';
import get from 'lodash/get';
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component';
import ModalCloseIcon from '../../global/svg/ModalCloseIcon.vue';
import SuccessCheck from './components/svg/SuccessCheck.vue';
import ErrorBang from './components/svg/ErrorBang.vue';
import FormButton from './components/dscl/FormButton.vue';

defineOptions({ name: 'FormModalV2' });

const CLOSE_DATE_FORMAT_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
  timeZoneName: 'short',
};

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
  questions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'close',
  'submitStart',
  'submitSuccess',
  'submitError',
  'fetchBrandStart',
  'fetchBrandSuccess',
  'fetchBrandError',
]);

const mainStore = useMainStore();
const alertBannerStore = useAlertBannerStore();
const { $axios, $d, $t } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();

const formTitleRef = ref(null);
const closeButtonRef = ref(null);

const answers = ref([]);
const brand = ref({});
const fetchingBrand = ref(false);
const fetchBrandSuccess = ref(false);
const hasFetchBrandError = ref(false);
const hasSubmissionError = ref(false);
const privacyPolicyUrl = ref('https://www.apptegy.com/privacy-policy/');
const privacyPolicyTarget = ref('_blank');
const submitting = ref(false);
const submitSuccess = ref(false);
const respondentEmail = ref(null);
const invalidOtpCode = ref(false);
const viewedAt = ref(new Date().toISOString());
const answersFormData = ref(new FormData());

const isAlertModalOpen = computed(() => alertBannerStore.isAlertModalOpen);
const headingColor = computed(() => mainStore.getThemeHeadingColor);
const overlayColor = computed(() => alertBannerStore.getThemeAlertShadowOverlay);

const cssVars = computed(() => {
  const themeColor = headingColor.value || '#333';
  const themeColorTwo = `${themeColor}05`;
  const themeColorFive = `${themeColor}13`;
  const themeColorTwenty = `${themeColor}33`;
  return {
    '--primary-forms-color': themeColor,
    '--light-forms-color': themeColorFive,
    '--medium-forms-color': themeColorTwenty,
    '--empty-forms-color': themeColorTwo,
    '--overlay-color': overlayColor.value,
  };
});

const initialFocusableItem = computed(() => document.querySelector('.form-modal-v2_container'));

const branding = computed(() => brand.value?.name || '');

const closeDate = computed(() => {
  const closeDateVal = props.form?.attributes?.close_date;
  if (!closeDateVal) return '';
  return $d(closeDateVal, CLOSE_DATE_FORMAT_OPTIONS);
});

const getDescription = computed(() => get(props.form, 'attributes.description', ''));

const shouldDisplayStatusView = computed(() => isNotAvailable.value || hasSubmissionError.value || submitSuccess.value);

const statusIconComponent = computed(() => {
  if (submitSuccess.value) return SuccessCheck;
  if (hasSubmissionError.value) return ErrorBang;
  return 'span';
});

const formTitle = computed(() => props.form?.title || props.form?.attributes?.name || '');

const parsedISODate = computed(() => parseISO(props.form?.attributes?.close_date));

const closeDateIsBeforeNow = computed(() => isBefore(parsedISODate.value, new Date()));

const isNotAvailable = computed(() => {
  const expirationDateISO = props.form?.attributes?.close_date;
  return !!expirationDateISO && closeDateIsBeforeNow.value;
});

const linkColor = computed(() => brand.value?.link_color || headingColor.value);

const logo = computed(() => brand.value?.logo || '');

const primaryColor = computed(() => brand.value?.primary_color || headingColor.value);

const questionsMap = computed(() => {
  if (!Array.isArray(props.questions)) return {};
  return props.questions.reduce(
    (acc, question) => ({
      ...acc,
      [question.id]: question,
    }),
    {},
  );
});

const statusText = computed(() => {
  if (isNotAvailable.value) return $t('forms.notAvailable');
  if (submitSuccess.value) return $t('forms.submitSuccess');
  if (hasSubmissionError.value) return $t('forms.submitError');
  return '';
});

const submitUrl = computed(() => {
  const uniqueId = props.form?.attributes?.unique_id;
  if (!uniqueId) return '';
  return `${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/forms/${uniqueId}/responses`;
});

function setAllowParentScroll(allowScroll) {
  if (!allowScroll && document.body.style.overflow === 'hidden') return;
  document.body.style.overflow = allowScroll ? 'visible' : 'hidden';
}

function setSubmitStatus(status, emitPayload) {
  switch (status) {
    case 'start':
      submitting.value = true;
      submitSuccess.value = false;
      hasSubmissionError.value = false;
      emit('submitStart', emitPayload);
      break;
    case 'success':
      submitSuccess.value = true;
      hasSubmissionError.value = false;
      submitting.value = false;
      emit('submitSuccess', emitPayload);
      break;
    case 'error':
      submitSuccess.value = false;
      hasSubmissionError.value = true;
      submitting.value = false;
      emit('submitError', emitPayload);
      break;
    case 'initial':
      submitSuccess.value = false;
      hasSubmissionError.value = false;
      submitting.value = false;
      break;
    default:
      break;
  }
}

function setFetchingBrandStatus(status, emitPayload) {
  switch (status) {
    case 'start':
      fetchingBrand.value = true;
      fetchBrandSuccess.value = false;
      hasFetchBrandError.value = false;
      emit('fetchBrandStart', emitPayload);
      break;
    case 'success':
      fetchBrandSuccess.value = true;
      hasFetchBrandError.value = false;
      fetchingBrand.value = false;
      emit('fetchBrandSuccess', emitPayload);
      break;
    case 'error':
      hasFetchBrandError.value = true;
      fetchBrandSuccess.value = false;
      fetchingBrand.value = false;
      emit('fetchBrandError', emitPayload);
      break;
    case 'initial':
      submitSuccess.value = false;
      hasFetchBrandError.value = false;
      fetchingBrand.value = false;
      break;
    default:
      break;
  }
}

function setAnswersToDefault() {
  answers.value = props.questions.map((question) => ({
    question_id: question.id,
    answer: '',
    accepted_at: getQuestionType(question.id) === 'E_SIGNATURE' ? '' : undefined,
  }));
}

function resetForm() {
  setAnswersToDefault();
  setSubmitStatus('initial');
}

function getQuestionType(questionId) {
  return questionsMap.value?.[questionId]?.elementType;
}

async function fetchBrand() {
  try {
    const brandId = props.form?.attributes?.brand_id;
    if (!brandId) return;
    setFetchingBrandStatus('start');
    const { data: brandData } = await $axios.get(
      `${runtimeConfig.public.ENGAGE_API_URL}/api/v1/public/brands/${brandId}`,
    );
    brand.value = brandData;
    setFetchingBrandStatus('success', brandData);
  } catch (error) {
    setFetchingBrandStatus('error', error);
  }
}

async function sendCode(email) {
  try {
    respondentEmail.value = respondentEmail.value || email;
    await $axios.post(`${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/email_verification`, {
      data: {
        attributes: {
          email: respondentEmail.value,
          form_unique_id: props.form?.attributes?.unique_id,
        },
      },
    });
  } catch (error) {
    console.error(error);
  }
}

async function confirmCode(code) {
  invalidOtpCode.value = false;
  try {
    const {
      data: { success },
    } = await $axios.post(`${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/otp_verification`, {
      data: {
        attributes: {
          email: respondentEmail.value,
          otp: code,
        },
      },
    });
    if (!success) {
      invalidOtpCode.value = true;
    } else {
      await handleFormSubmit();
    }
  } catch (error) {
    console.error(error);
  }
}

async function handleFormSubmit() {
  try {
    if (submitting.value) return;
    setSubmitStatus('start');
    if (!submitUrl.value) throw new Error('Not a valid submit url');
    const answersPayload = answersFormData.value;
    answersPayload.append('data[attributes][viewed_at]', viewedAt.value);

    const submitRequest = await $axios.post(submitUrl.value, answersPayload, {
      headers: {
        'X-otp-email': respondentEmail.value,
        'Content-Type': 'multipart/form-data',
      },
    });
    if (submitRequest.status !== 200) {
      throw new Error('Form submission not successful');
    }
    setSubmitStatus('success');
  } catch (error) {
    setSubmitStatus('error', error);
  }
  closeButtonRef.value?.focus();
}

function onKeydown(e) {
  if (e.key === 'Escape') {
    emit('close');
  }
}

function handleModalClose() {
  if (submitting.value) return;
  emit('close');
}

onMounted(() => {
  fetchBrand();
  setAnswersToDefault();
  setAllowParentScroll(false);
  nextTick(() => {
    if (formTitleRef.value) {
      formTitleRef.value.innerText = formTitle.value;
    }
  });
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  setAllowParentScroll(true);
  window.removeEventListener('keydown', onKeydown);
});
</script>
<style lang="scss" scoped>
.form-modal-v2 {
  --modalIndex: 99;
  z-index: var(--modalIndex, 99);
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &::before {
    content: '';
    z-index: -1;
    position: absolute;
    left: 0;
    right: 0;
    display: block;
    width: 100%;
    height: 100%;
    opacity: 0.9;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), var(--overlay-color);
  }

  :deep(a) {
    color: var(--form-link-color) !important; // important added to override tailwind classes inside fc-form-renderer
  }
}
.form-modal-v2_container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 960px;
  height: 100%;
  max-height: 800px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.06);

  @media screen and (max-width: 599px) {
    max-height: 100%;
  }
}
.form-modal-v2_controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 40px;
}
.close-icon {
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  margin-left: auto;
}
.form-modal-v2_close {
  margin-left: auto;
}
.form-modal-v2_form {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-left: 0;
  padding-right: 0;
}

.form-modal-v2_status-display {
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.form-modal-v2_status-icon {
  margin-bottom: 24px;
}
.form-modal-v2_status-text {
  display: inline-block;
  font-size: 24px;
  line-height: 32px;
  font-weight: 900;
  letter-spacing: 0.8px;
}
.form-modal-v2_title {
  font-size: 32px;
  margin-bottom: 24px;
  margin-bottom: auto;
  font-weight: bold;
}
.form-modal-v2_privacy-policy {
  display: inline-block;
  margin-bottom: 64px;
  &,
  &:visited,
  &:active {
    color: var(--form-link-color);
  }
}
.form-modal-v2_status-content {
  margin-bottom: auto;
}
.form-modal-v2_status-action {
  display: block;
  width: 100%;
  max-width: 350px;
  padding: 16px 6px;
  margin: 7vh auto 2vh;
  font-size: 20px;
  line-height: 1rem;
  color: white;
  border-radius: 3px;

  &:before {
    display: none;
  }

  &,
  &:focus {
    border: 0;
    background: var(--primary-forms-color);
  }
}
</style>
